<template>
    <v-col v-bind="columnProps">
        <v-card class="fill-height">
            <v-toolbar color="grey-lighten-4" density="compact" flat>
                <template #title>{{ displayTitle }}</template>
            </v-toolbar>

            <v-divider />

            <v-card-text class="dashboard-widget-error">
                <v-alert class="mb-0" type="error" variant="tonal">
                    {{ errorMessage }}
                </v-alert>
            </v-card-text>
        </v-card>
    </v-col>
</template>

<script setup lang="ts">
/*
Organization / ordering applied:
1. Imports
2. Props / Emits
3. Constants
4. Stores & top-level services
5. Reactive state (refs/reactive)
6. Validation rules & setup
7. Computed
8. Handlers / methods
9. Helpers / lifecycle
*/

// 1. Imports
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import type { DashboardWidget } from '@/stores/dashboard'
import {
    DASHBOARD_WIDGET_COLUMNS,
    type DashboardWidgetSize,
    getDashboardWidgetDisplayTitle
} from '@/views/Dashboard/dashboardUtils'

// 2. Props / Emits
const props = withDefaults(
    defineProps<{
        widget: DashboardWidget
        size?: DashboardWidgetSize
    }>(),
    { size: 'small' }
)

// 4. Stores & top-level services
const { t } = useI18n()

// 7. Computed
const columnProps = computed(() => (props.size === 'large' ? DASHBOARD_WIDGET_COLUMNS.LARGE : DASHBOARD_WIDGET_COLUMNS.SMALL))
const displayTitle = computed(() => getDashboardWidgetDisplayTitle(props.widget))

const errorMessage = computed(() =>
    t('common.dashboard.errors.fetchWidgetFailed', {
        codeName: getDashboardWidgetDisplayTitle(props.widget)
    })
)
</script>

<style scoped>
.dashboard-widget-error {
    min-height: 120px;
}
</style>
